// src/pages/gt3x/Reports.jsx
import { useEffect, useMemo, useState } from "react";
import RaceSessionSelect from "../../components/RaceSessionSelect";
import { safeFetchSheet } from "../../lib/sheetsSafe";

// Wir holen immer den kompletten Bereich, weil wir für REKO aus beiden Blöcken lesen
function rangeFor(race) {
    return `R${race}_INCIDENTS!A1:Z300`;
}

// Spaltenhilfen: A -> 0, B -> 1, ..., Z -> 25
function colIndexFromLetters(letters) {
    let n = 0;
    const s = String(letters).toUpperCase().trim();
    for (let i = 0; i < s.length; i++) {
        const code = s.charCodeAt(i);
        if (code < 65 || code > 90) continue; // nur A-Z
        n = n * 26 + (code - 64);
    }
    return n - 1; // 0-based
}

// Subset: bestimmte Spalten aus Header + Zeilen herausziehen
// Subset: bestimmte Spalten aus Header + Zeilen herausziehen
// Subset: bestimmte Spalten aus Header + Zeilen herausziehen
function subsetByLetters(header, rows, letters) {
    const indices = letters.map(colIndexFromLetters);

    const safeHeader = Array.isArray(header) ? header : [];

    // Header: wenn an der Stelle nichts steht, ersatzweise den Spaltenbuchstaben verwenden
    const subHeader = indices.map((idx, k) => {
        const cell = safeHeader[idx];
        return cell !== undefined && cell !== null && String(cell).trim() !== ""
            ? cell
            : letters[k]; // z.B. "B", "F" als Fallback
    });

    const safeRows = Array.isArray(rows) ? rows : [];

    // Rows: falls eine Zeile kürzer ist, einfach leeren String einsetzen
    const subRows = safeRows.map((row) =>
        indices.map((idx) => (row && row[idx] !== undefined ? row[idx] : ""))
    );

    return { header: subHeader, rows: subRows };
}


// Führende komplett leere Zeilen entfernen
function trimLeadingEmptyRows(rows) {
    if (!Array.isArray(rows)) return [];
    let start = 0;
    while (start < rows.length) {
        const r = rows[start] || [];
        const hasValue = r.some(
            (cell) =>
                cell !== null &&
                cell !== undefined &&
                String(cell).trim() !== ""
        );
        if (hasValue) break;
        start++;
    }
    return rows.slice(start);
}

// Einfache Table-Komponente mit Placeholder, wenn keine Rows
function SimpleTable({ header, rows, emptyMessage, highlightCols }) {
    if (!header || header.length === 0) return null;

    const hasRows = Array.isArray(rows) && rows.length > 0;
    const highlightSet = Array.isArray(highlightCols) ? new Set(highlightCols) : null;

    return (
        <div className="fancyTable">
            <table className="tbl">
                <thead>
                <tr>
                    {header.map((h, i) => (
                        <th key={i}>{h}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {hasRows ? (
                    rows.map((r, ri) => (
                        <tr key={ri}>
                            {r.map((cell, ci) => {
                                const numeric =
                                    typeof cell === "number" || !isNaN(Number(cell));
                                const numVal = Number(cell);
                                const shouldHighlight =
                                    highlightSet &&
                                    highlightSet.has(ci) &&
                                    numeric &&
                                    numVal > 0;

                                return (
                                    <td
                                        key={ci}
                                        className={
                                            numeric ? "num" : ""
                                        }
                                        style={
                                            shouldHighlight
                                                ? { color: "#ff6b6b", fontWeight: 600 }
                                                : undefined
                                        }
                                    >
                                        {cell}
                                    </td>
                                );
                            })}
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td
                            colSpan={header.length}
                            className="align-left text-dim"
                        >
                            {emptyMessage || "Noch keine Einträge vorhanden."}
                        </td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
    );
}


export default function Reports() {
    const [race, setRace] = useState(1);
    const [view, setView] = useState("INCIDENTS"); // "INCIDENTS" | "PROTESTS" | "REKO"
    const [trackLabel, setTrackLabel] = useState("");

    const [data, setData] = useState({ header: [], rows: [] });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            setLoading(true);
            setErr("");

            const range = rangeFor(race);
            const res = await safeFetchSheet(range);

            if (!res.ok) {
                setErr(res.message || "Fehler beim Laden.");
                setData({ header: [], rows: [] });
                setLoading(false);
                return;
            }

            const d = res.data;
            let header = [];
            let rows = [];

            // Verschiedene mögliche Antwortformen von safeFetchSheet auffangen
            if (Array.isArray(d?.header) && Array.isArray(d?.rows)) {
                header = d.header;

                if (Array.isArray(d.rows[0])) {
                    // rows bereits als Matrix
                    rows = d.rows;
                } else {
                    // rows als Objekte -> in Matrix umwandeln
                    rows = d.rows.map((rowObj) => header.map((h) => rowObj[h]));
                }
            } else if (Array.isArray(d?.values) && d.values.length) {
                const [head, ...body] = d.values;
                header = head;
                rows = body;
            } else {
                header = [];
                rows = [];
            }

            setData({ header, rows });
            setLoading(false);
        })();
    }, [race]); // wichtig: View ändert nur die Darstellung, nicht den Fetch

    // Tabellen je nach View vorbereiten – direkt mit data.header / data.rows
    const incidentsTable = useMemo(() => {
        if (view !== "INCIDENTS") return null;
        const header = data.header ?? [];
        const rows = data.rows ?? [];
        // Incident-Reports: Spalten A..E
        return subsetByLetters(header, rows, ["A", "B", "C", "D", "E"]);
    }, [view, data.header, data.rows]);

    const protestsTable = useMemo(() => {
        if (view !== "PROTESTS") return null;
        const header = data.header ?? [];
        const rows = data.rows ?? [];
        // Proteste: Block L..R
        const subset = subsetByLetters(header, rows, ["L", "M", "N", "O", "P", "Q", "R"]);
        return {
            header: subset.header,
            rows: trimLeadingEmptyRows(subset.rows),
        };
    }, [view, data.header, data.rows]);

    const rekoTableIncidents = useMemo(() => {
        if (view !== "REKO") return null;
        const header = data.header ?? [];
        const rows = data.rows ?? [];

        // ReKo-Entscheidungen zu Incidents: A, F, G, H, I
        const subset = subsetByLetters(header, rows, ["A", "F", "G", "H", "I"]);
        return {
            header: subset.header,
            rows: trimLeadingEmptyRows(subset.rows),
        };
    }, [view, data.header, data.rows]);

    const rekoTableProtests = useMemo(() => {
        if (view !== "REKO") return null;
        const header = data.header ?? [];
        const rows = data.rows ?? [];
        // ReKo-Entscheidungen zu Fahrerprotesten: L, S, T, U, V
        const subset = subsetByLetters(header, rows, ["L", "S", "T", "U", "V"]);
        return {
            header: subset.header,
            rows: trimLeadingEmptyRows(subset.rows),
        };
    }, [view, data.header, data.rows]);

    const viewLabel =
        view === "INCIDENTS"
            ? "Incidents"
            : view === "PROTESTS"
                ? "Protests"
                : "ReKo Entscheidungen";

    return (
        <>
            {/* Nur Rennen auswählen – Session-Dropdown ausgeblendet */}
            <RaceSessionSelect
                valueRace={race}
                onRaceChange={setRace}
                showSession={false}
                onTrackLabelChange={setTrackLabel}
            />

            <div
                className="card"
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <h2>Rennkommission</h2>
                    <p className="muted">
                        {trackLabel} || <strong>{viewLabel}</strong>
                    </p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        className={`button ${
                            view === "INCIDENTS" ? "button--primary" : "button--secondary"
                        }`}
                        onClick={() => setView("INCIDENTS")}
                    >
                        Incidents
                    </button>
                    <button
                        className={`button ${
                            view === "PROTESTS" ? "button--primary" : "button--secondary"
                        }`}
                        onClick={() => setView("PROTESTS")}
                    >
                        Proteste
                    </button>
                    <button
                        className={`button ${
                            view === "REKO" ? "button--primary" : "button--secondary"
                        }`}
                        onClick={() => setView("REKO")}
                    >
                        Reko-Entscheidungen
                    </button>
                </div>
            </div>

            {err && (
                <div className="card">
                    <p className="muted">{err}</p>
                </div>
            )}
            {loading && (
                <div className="card">
                    <p>Lade…</p>
                </div>
            )}

            {!loading && !err && (
                <>
                    {/* INCIDENTS: eine Tabelle */}
                    {view === "INCIDENTS" && incidentsTable && (
                        <div className="reports-table">
                            <SimpleTable
                                header={incidentsTable.header}
                                rows={incidentsTable.rows}
                                emptyMessage="Noch keine Incidents eingetragen."
                            />
                        </div>
                    )}

                    {/* PROTESTS: eine Tabelle */}
                    {view === "PROTESTS" && protestsTable && (
                        <div className="reports-table">
                            <SimpleTable
                                header={protestsTable.header}
                                rows={protestsTable.rows}
                                emptyMessage="Noch keine Fahrerproteste eingereicht."
                            />
                        </div>
                    )}

                    {/* REKO: zwei Tabellen untereinander */}
                    {view === "REKO" && (
                        <>
                            <div className="card">
                                <h3>ReKo Entscheidungen zu Incident Reports</h3>
                            </div>
                            <div className="reports-table">
                                {rekoTableIncidents && (
                                    <SimpleTable
                                        header={rekoTableIncidents.header}
                                        rows={rekoTableIncidents.rows}
                                        emptyMessage="Noch keine ReKo-Entscheidungen zu Incident Reports."
                                        highlightCols={[3]}   // H = 4. Spalte im Subset
                                    />
                                )}
                            </div>

                            <div className="card" style={{ marginTop: 30 }}>
                                <h3>ReKo Entscheidungen zu Fahrerprotesten</h3>
                            </div>
                            <div className="reports-table">
                                {rekoTableProtests && (
                                    <SimpleTable
                                        header={rekoTableProtests.header}
                                        rows={rekoTableProtests.rows}
                                        emptyMessage="Noch keine ReKo-Entscheidungen zu Fahrerprotesten."
                                        highlightCols={[3]}   // U = 4. Spalte im Subset
                                    />
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
}
